// app/frontend/components/ui/Auth/AuthSubmitButton.tsx
import React from 'react';
import ThemedButton, { type ThemedButtonProps } from '../Buttons/ThemedButton';

/**
 * `type`, `fullWidth` and `size` are deliberately not overridable: the whole
 * point of this component is that every auth page submits identically. Without
 * the Omit, `{...props}` spreading after them would let a caller silently undo
 * the normalization this component exists to guarantee.
 */
export type AuthSubmitButtonProps = Omit<ThemedButtonProps, 'type' | 'fullWidth' | 'size'>;

/**
 * Normalized submit button for the five auth pages. Wraps ThemedButton with
 * Login's gradient treatment (the nicest of the five, already in production)
 * so every auth page gets the same look and hover behavior for free.
 */
export const AuthSubmitButton = React.forwardRef<HTMLButtonElement, AuthSubmitButtonProps>(
  ({ loading, disabled, children, style, onMouseEnter, onMouseLeave, ...props }, ref) => {
    return (
      <ThemedButton
        ref={ref}
        type="submit"
        fullWidth
        size="md"
        loading={loading}
        disabled={disabled}
        style={{
          background: 'linear-gradient(135deg, #EA580C 0%, #C2410C 50%, #F97316 100%)',
          border: 'none',
          boxShadow: '0 0 15px rgba(234, 88, 12, 0.4)',
          transition: 'all 0.3s ease',
          fontWeight: 600,
          ...style,
        }}
        onMouseEnter={(e) => {
          if (!loading) {
            e.currentTarget.style.boxShadow = '0 0 25px rgba(234, 88, 12, 0.6)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }
          onMouseEnter?.(e);
        }}
        onMouseLeave={(e) => {
          if (!loading) {
            e.currentTarget.style.boxShadow = '0 0 15px rgba(234, 88, 12, 0.4)';
            e.currentTarget.style.transform = 'translateY(0)';
          }
          onMouseLeave?.(e);
        }}
        {...props}
      >
        {children}
      </ThemedButton>
    );
  },
);

AuthSubmitButton.displayName = 'AuthSubmitButton';

export default AuthSubmitButton;
